import { useState, useRef, useEffect } from "react";
import { API } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bot, Send, User } from "lucide-react";

const SUGGESTIONS = [
  "How many bills are pending today?",
  "Which bill took the longest to pack?",
  "Why is dispatch delayed today?",
  "Which warehouse employee handled the highest quantity?",
  "Which vehicles are waiting too long right now?",
  "Where are we losing warehouse time?",
];

export default function Assistant() {
  const [messages, setMessages] = useState([
    { id: 0, role: "assistant", text: "I have live access to today's warehouse data — bills, tasks, vehicles, and worker productivity. Ask me anything." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const idRef = useRef(1);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const ask = async (question) => {
    const q = (question ?? input).trim();
    if (!q || streaming) return;
    setInput("");
    setMessages((m) => [...m, { id: idRef.current++, role: "user", text: q }, { id: idRef.current++, role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/ai/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ question: q, session_id: "main" }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], text: copy[copy.length - 1].text + obj.delta };
                return copy;
              });
            } else if (obj.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], text: `Error: ${obj.error}` };
                return copy;
              });
            }
          } catch (err) {
            console.error("Assistant stream parse error:", err);
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], text: "Failed to reach the assistant. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-10rem)]" data-testid="assistant-page">
      <SectionTitle>AI Management Assistant</SectionTitle>
      <div className="flex flex-wrap gap-2 mb-3">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            onClick={() => ask(s)}
            disabled={streaming}
            className="text-xs border border-border rounded-full px-3 py-1.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150 disabled:opacity-40"
            data-testid={`suggestion-${s.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}
          >
            {s}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto space-y-4 bg-card border border-border rounded-lg p-4" data-testid="chat-messages">
        {messages.map((m, i) => (
          <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "assistant" && (
              <div className="w-7 h-7 rounded-full bg-primary/20 text-primary flex items-center justify-center shrink-0">
                <Bot className="w-4 h-4" />
              </div>
            )}
            <div className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm whitespace-pre-wrap ${
              m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"
            }`} data-testid={`chat-message-${i}`}>
              {m.text || (streaming && i === messages.length - 1 ? "…" : "")}
            </div>
            {m.role === "user" && (
              <div className="w-7 h-7 rounded-full bg-secondary text-muted-foreground flex items-center justify-center shrink-0">
                <User className="w-4 h-4" />
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="flex gap-2 mt-3">
        <Input
          data-testid="chat-input"
          placeholder="Ask about bills, delays, workers, vehicles…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          disabled={streaming}
        />
        <Button onClick={() => ask()} disabled={streaming} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="chat-send-button">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
